import { HealicMascot } from "@/components/HealicMascot";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { HeroBookingForm } from "@/components/HeroBookingForm";
import { surgeries, specialties, contact, locations, type Surgery, type Specialty } from "@/lib/healic-data";
import { getSurgeryClinicalContent } from "@/lib/condition-content";
import { absUrl, BRAND, breadcrumbJsonLd, surgerySeo } from "@/lib/seo";
import { iconForSymptom, iconForCause } from "@/lib/content-icons";
import {
  ArrowRight,
  Check,
  Phone,
  MapPin,
  Activity,
  AlertCircle,
  Stethoscope,
  HeartPulse,
  HeartHandshake,
  ShieldCheck,
  Microscope,
  Quote,
  Dna,
  Utensils,
  CigaretteOff,
  Hourglass,
  Thermometer,
  Droplets,
  Zap,
  Moon,
  Frown,
  CircleDot,
  Star,
  Award,
  ChevronDown,
  Scissors,
  PlusCircle,
  Timer,
  Lock,
  ClipboardCheck,
  Wallet,
  ShieldPlus,
  Activity as ActivityIcon,
  LogOut,
} from "lucide-react";
import { useMergedDoctors } from "@/lib/use-merged-doctors";
import { doctorProfileSlug } from "@/lib/old-doctor-slugs";
import { DoctorPortrait } from "@/components/DoctorPortrait";
import surgeryHero from "@/assets/surgery-hero.jpg";
import surgeryProcedure from "@/assets/surgery-procedure.jpg";
import specGeneral from "@/assets/specialty-general-surgery.jpg";
import specGynae from "@/assets/specialty-gynaecology.jpg";
import specOrtho from "@/assets/specialty-orthopaedics.jpg";
import specUro from "@/assets/specialty-urology.jpg";
import specEnt from "@/assets/specialty-ent.jpg";
import specEye from "@/assets/specialty-ophthalmology.jpg";
import surgeryCSection from "@/assets/surgery-c-section.jpg";
import surgeryNormalDelivery from "@/assets/surgery-normal-delivery.jpg";
import surgeryHernia from "@/assets/surgery-hernia.jpg";
import surgeryCataract from "@/assets/surgery-cataract.jpg";
import surgeryGallbladder from "@/assets/surgery-gallbladder.jpg";
import surgeryPiles from "@/assets/surgery-piles.jpg";
import surgeryKnee from "@/assets/surgery-knee-replacement.jpg";
import surgeryCircumcision from "@/assets/surgery-circumcision.jpg";
import surgeryVaricose from "@/assets/surgery-varicose-veins.jpg";
import surgeryAppendix from "@/assets/surgery-appendectomy.jpg";
import surgeryHysterectomy from "@/assets/surgery-hysterectomy.jpg";
import surgeryOvarian from "@/assets/surgery-ovarian-cyst.jpg";
import surgeryHip from "@/assets/surgery-hip-replacement.jpg";
import surgeryFracture from "@/assets/surgery-fracture-orif.jpg";
import surgeryArthroscopy from "@/assets/surgery-arthroscopy.jpg";
import surgeryKidneyStone from "@/assets/surgery-kidney-stone.jpg";
import surgeryProstate from "@/assets/surgery-prostate.jpg";
import surgeryHydrocele from "@/assets/surgery-hydrocele.jpg";
import surgeryTonsils from "@/assets/surgery-tonsillectomy.jpg";
import surgerySinus from "@/assets/surgery-sinus.jpg";
import surgerySeptoplasty from "@/assets/surgery-septoplasty.jpg";
import gsHernia from "@/assets/category-hero/hernia.jpeg.asset.json";
import gsPiles from "@/assets/category-hero/piles.jpg.asset.json";
import gsCircumcision from "@/assets/category-hero/circumcision.jpg.asset.json";
import gsVaricose from "@/assets/category-hero/varicose.jpg.asset.json";
import gsLipoma from "@/assets/category-hero/lipoma.jpg.asset.json";
import gsFissure from "@/assets/category-hero/fissure.jpg.asset.json";
import gsFistula from "@/assets/category-hero/fistula.jpg.asset.json";
import journey35 from "@/assets/journey/journey-35.jpeg.asset.json";
import journey34 from "@/assets/journey/journey-34.jpeg.asset.json";
import journey33 from "@/assets/journey/journey-33.jpeg.asset.json";
import journey32 from "@/assets/journey/journey-32.jpeg.asset.json";
import journey31 from "@/assets/journey/journey-31.jpeg.asset.json";
import { PromiseBand } from "@/components/PromiseBand";

const specialtyImage: Record<string, string> = {
  "general-surgery": specGeneral,
  "gynaecology": specGynae,
  "orthopaedics": specOrtho,
  "urology": specUro,
  "ent": specEnt,
  "ophthalmology": specEye,
};

const surgeryImage: Record<string, string> = {
  "piles-hemorrhoidectomy": gsPiles.url,
  "hernia-repair": gsHernia.url,
  "gallbladder-removal": surgeryGallbladder,
  "circumcision": gsCircumcision.url,
  "varicose-veins": gsVaricose.url,
  "lipoma-removal": gsLipoma.url,
  "anal-fissure": gsFissure.url,
  "anal-fistula": gsFistula.url,
  "appendectomy": surgeryAppendix,
  "normal-c-section-delivery": surgeryCSection,
  "hysterectomy": surgeryHysterectomy,
  "ovarian-cyst-removal": surgeryOvarian,
  "knee-replacement": surgeryKnee,
  "hip-replacement": surgeryHip,
  "fracture-orif": surgeryFracture,
  "arthroscopy": surgeryArthroscopy,
  "kidney-stone-removal": surgeryKidneyStone,
  "prostate-turp": surgeryProstate,
  "hydrocele-surgery": surgeryHydrocele,
  "tonsillectomy": surgeryTonsils,
  "sinus-surgery-fess": surgerySinus,
  "septoplasty": surgerySeptoplasty,
  "cataract-surgery": surgeryCataract,
};
// Reserved for future use
void surgeryNormalDelivery;
void surgeryHernia;
void surgeryPiles;
void surgeryCircumcision;
void surgeryVaricose;

function padFaqs(s: Surgery): { q: string; a: string }[] {
  const generic = [
    { q: `Is ${s.name} safe at Healic?`, a: `Yes. ${s.name} at Healic is performed by experienced senior specialists using modern equipment and strict sterilisation protocols, with full pre-op evaluation and post-op monitoring.` },
    { q: `How long does the ${s.name.toLowerCase()} procedure take?`, a: `Most procedures are completed within 30–90 minutes depending on complexity. Your surgeon will share an exact time estimate during consultation.` },
    { q: `What is the recovery time?`, a: s.recovery || `Most patients resume light daily activities within a few days and return to normal routine in 1–2 weeks, with full recovery guided by your surgeon.` },
    { q: `Is ${s.name} covered by insurance?`, a: `Yes — we are empanelled with 50+ insurance partners and offer cashless support for eligible policies, plus easy EMI options if needed.` },
    { q: `Will I need to stay in the hospital?`, a: `Many procedures are day-care or require a short 1–2 day stay. Your care team will confirm based on your specific case and recovery progress.` },
    { q: `What are the risks or side effects?`, a: `Modern techniques have made this procedure very safe. Minor risks like temporary soreness or swelling are normal — your surgeon will discuss any specific risks in detail.` },
  ];
  const existing = s.faqs ?? [];
  const have = new Set(existing.map((f) => f.q.toLowerCase()));
  const extras = generic.filter((g) => !have.has(g.q.toLowerCase()));
  return [...existing, ...extras].slice(0, Math.max(5, existing.length));
}


const causeItems: { icon: typeof Activity; tint: string; text: string }[] = [
  { icon: Dna, tint: "from-rose-500/15 to-rose-500/0 text-rose-600", text: "Genetic or hereditary predisposition" },
  { icon: Utensils, tint: "from-amber-500/15 to-amber-500/0 text-amber-600", text: "Lifestyle factors — diet, posture or daily activity" },
  { icon: HeartPulse, tint: "from-emerald-500/15 to-emerald-500/0 text-emerald-600", text: "Underlying medical conditions" },
  { icon: Hourglass, tint: "from-sky-500/15 to-sky-500/0 text-sky-600", text: "Age-related wear & tear" },
  { icon: CigaretteOff, tint: "from-violet-500/15 to-violet-500/0 text-violet-600", text: "Smoking, alcohol or environmental triggers" },
  { icon: AlertCircle, tint: "from-orange-500/15 to-orange-500/0 text-orange-600", text: "Previous untreated infections or injuries" },
];

const symptomItems: { icon: typeof Activity; text: string }[] = [
  { icon: Thermometer, text: "Persistent pain or discomfort" },
  { icon: CircleDot, text: "Visible swelling or change in tissue" },
  { icon: Frown, text: "Difficulty performing daily activities" },
  { icon: Zap, text: "Sudden flare-ups or sharp episodes" },
  { icon: Moon, text: "Disturbed sleep or constant fatigue" },
  { icon: Droplets, text: "Unusual discharge, bleeding or fever" },
];

const benefitStrip: { icon: typeof Scissors; label: string }[] = [
  { icon: Scissors, label: "Minimally Invasive Options" },
  { icon: PlusCircle, label: "Day-Care Procedure" },
  { icon: Timer, label: "Quick Recovery" },
  { icon: ShieldCheck, label: "Safe & Effective" },
  { icon: Lock, label: "Low Recurrence Risk" },
  { icon: Award, label: "Expert Surgeons" },
];

const journeySteps: { title: string; img: string }[] = [
  { title: "Proper Counselling", img: journey31.url },
  { title: "Fixed Price Explanation", img: journey32.url },
  { title: "Safe Surgery", img: journey33.url },
  { title: "Structured Monitoring", img: journey34.url },
  { title: "Time-Bound Discharge", img: journey35.url },
];

const surgeryTypes: { title: string; img: string }[] = [
  { title: "Open Surgery", img: surgeryHero },
  { title: "Laparoscopic", img: surgeryProcedure },
  { title: "Robotic Assisted", img: specGeneral },
  { title: "Single-Incision", img: surgeryGallbladder },
];

function defaultReviews(s: Surgery) {
  return [
    { name: "Priya M.", text: `Very transparent pricing and caring staff. Highly recommend Healic for ${s.name.toLowerCase()}.` },
    { name: "Rahul S.", text: `Got my ${s.name.toLowerCase()} done at Healic. The team was super supportive and the recovery was much faster than I expected.` },
    { name: "Anil K.", text: `Doctors explained everything patiently. The surgery went smooth and I was discharged on time.` },
    { name: "Sunita R.", text: `Excellent experience from consultation to discharge. The fixed-price package gave me complete peace of mind.` },
  ];
}

export type SurgeryLoaderData = {
  surgery: Surgery;
  specialty: Specialty | undefined;
  related: Surgery[];
  extraSections?: React.ReactNode;
};


export const Route = createFileRoute("/surgeries/$slug")({
  loader: ({ params }): SurgeryLoaderData => {
    const surgery = surgeries.find((s) => s.slug === params.slug);
    if (!surgery) throw notFound();
    const specialty = specialties.find((sp) => sp.slug === surgery.specialtySlug);
    const related = surgeries
      .filter((s) => s.specialtySlug === surgery.specialtySlug && s.slug !== surgery.slug)
      .slice(0, 4);
    return { surgery, specialty, related };
  },
  head: ({ loaderData, params }) => {
    const s = loaderData?.surgery;
    const sp = loaderData?.specialty;
    if (!s) return { meta: [{ title: "Surgery — Healic" }] };
    const path = `/surgeries/${s.slug}`;
    const url = absUrl(path);
    const seo = surgerySeo({ name: s.name, slug: s.slug, short: s.short, overview: s.overview, specialtyName: sp?.name });
    const isLocationPage = "location" in params;
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
        { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:title", content: `${seo.shortName} — ${BRAND}` },
        { property: "og:description", content: seo.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_IN" },
        { property: "og:site_name", content: BRAND },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${seo.shortName} — Healic` },
        { name: "twitter:description", content: seo.description },
        { property: "og:image", content: "https://healic.in/og-image.jpg" },
        { property: "og:image:alt", content: `${seo.shortName} at Healic Multispeciality Hospital` },
        { name: "twitter:image", content: "https://healic.in/og-image.jpg" },
      ],
      // Child location routes publish their own city-specific canonical.
      // Omitting the parent canonical there prevents two competing tags.
      links: isLocationPage ? [] : [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalProcedure",
            name: s.name,
            alternateName: seo.shortName,
            description: s.overview,
            procedureType: "https://schema.org/SurgicalProcedure",
            howPerformed: s.procedure.join(". "),
            followup: s.recovery,
            url,
            ...(sp ? { relevantSpecialty: { "@type": "MedicalSpecialty", name: sp.name } } : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: s.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        breadcrumbJsonLd([
          { name: "Home", url: absUrl("/") },
          { name: "Surgeries", url: absUrl("/surgeries") },
          ...(sp ? [{ name: sp.name, url: absUrl(`/specialties/${sp.slug}`) }] : []),
          { name: seo.shortName, url },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <PageShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Surgery not found</h1>
        <Link to="/surgeries" className="mt-6 inline-block text-accent">Browse all surgeries →</Link>
      </div>
    </PageShell>
  ),
  component: () => <SurgeryPage data={Route.useLoaderData()} />,
});

export function SurgeryPage({ data }: { data: SurgeryLoaderData }) {
  const { surgery, specialty, related, extraSections } = data;

  const allDoctors = useMergedDoctors();
  const doctors = allDoctors
    .filter((d) => d.specialtySlug === surgery.specialtySlug)
    .sort((a, b) => (parseInt(String(b.experience ?? "").match(/\d+/)?.[0] ?? "0", 10)) - (parseInt(String(a.experience ?? "").match(/\d+/)?.[0] ?? "0", 10)));
  const heroBg = surgeryImage[surgery.slug] ?? specialtyImage[surgery.specialtySlug] ?? surgeryHero;
  const reviewsList = defaultReviews(surgery);
  const reviews = defaultReviews(surgery);
  const faqs = padFaqs(surgery);
  const clinical = getSurgeryClinicalContent(surgery.slug);
  const hideSymptomsCauses = surgery.slug === "normal-c-section-delivery";
  const symptomList = clinical?.symptoms ?? symptomItems.map((item) => item.text);
  const causeList = clinical?.causes ?? causeItems.map((item) => item.text);
  const typeList = clinical?.types ?? surgeryTypes.map((item) => item.title);

  return (
    <PageShell>
      {/* HERO — matches specialty layout */}
      <section className="relative isolate overflow-hidden bg-primary text-primary-foreground">
        <img src={heroBg} alt="" aria-hidden fetchPriority="high" decoding="async" width={780} height={520} className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/85 to-primary/40" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-6 lg:px-8 lg:py-8">
          <nav className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-accent">
            <Link to="/surgeries" className="hover:opacity-80">← Surgeries</Link>
            {specialty && (
              <>
                <span className="opacity-50">/</span>
                <Link to="/specialties/$slug" params={{ slug: specialty.slug }} className="hover:opacity-80">
                  {specialty.name}
                </Link>
              </>
            )}
          </nav>
          <div className="mt-3 grid items-start gap-6 lg:grid-cols-[1.35fr_1fr]">
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                {surgery.name}
              </h1>
              <p className="mt-3 max-w-xl text-sm opacity-90 lg:text-base">{surgery.short}</p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-accent-foreground hover:opacity-90">
                  Book consultation <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a href={`tel:${contact.phoneTel}`} className="inline-flex items-center gap-2 rounded-pill bg-white/10 px-5 py-2.5 text-xs font-semibold ring-1 ring-white/25 backdrop-blur">
                  <Phone className="h-3.5 w-3.5 text-accent" /> {contact.phone}
                </a>
              </div>

              {/* Trust stats inside hero */}
              <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
                {[
                  { icon: Award, big: "5,000+", small: "Successful Surgeries" },
                  { icon: Star, big: "Average 4.8", small: "Patient Rating" },
                  { icon: Stethoscope, big: "85+ NPS", small: "Happy Patients" },
                  { icon: ShieldCheck, big: "Insurance", small: "All Accepted" },
                ].map(({ icon: Icon, big, small }) => (
                  <div key={small} className="flex items-start gap-2">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <div>
                      <p className="font-display text-xs font-bold leading-tight sm:text-sm">{big}</p>
                      <p className="mt-0.5 text-[10px] leading-snug opacity-80">{small}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Feature badges — fills left column to match form height */}
              <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                {[
                  { icon: Wallet, label: "0% EMI Available" },
                  { icon: ShieldPlus, label: "Cashless Insurance" },
                  { icon: Timer, label: "Single Point Customer Support" },
                  { icon: ClipboardCheck, label: "Free Consultation" },
                  { icon: Scissors, label: "Advanced Tech" },
                  { icon: PlusCircle, label: "Pickup & Drop" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 ring-1 ring-white/15 backdrop-blur">
                    <Icon className="h-4 w-4 shrink-0 text-accent" />
                    <span className="text-[11px] font-semibold leading-tight">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <HeroBookingForm tagline={`Get expert care for ${surgery.name}`} />
            </div>
          </div>
        </div>
      </section>


      {/* PROMISE BAND */}
      <PromiseBand />


      {/* ABOUT — title + body + image */}
      <section className="mx-auto max-w-6xl px-4 pb-10 pt-2 lg:px-8 lg:pb-14">
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">About {surgery.name}</h2>
          {specialty && (
            <p className="mt-1 text-sm font-semibold text-muted-foreground">({specialty.name})</p>
          )}
        </div>
        <div className="mt-8 grid items-stretch gap-8 lg:grid-cols-[1.7fr_1fr]">
          <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground">
            <p>{surgery.overview}</p>
            {!hideSymptomsCauses && (
              <p>
                Patients usually need evaluation for {symptomList.slice(0, 3).join(", ").toLowerCase()}. Our team confirms causes such as {causeList.slice(0, 3).join(", ").toLowerCase()} and recommends the right option from {typeList.join(", ").toLowerCase()} with transparent pricing and structured follow-up.
              </p>
            )}
          </div>
          <div className="overflow-hidden rounded-2xl border border-border shadow-soft self-start">
            <img src={heroBg} alt={surgery.name} loading="lazy" width={780} height={520} className="h-full max-h-[340px] w-full object-cover" />
          </div>
        </div>
      </section>

      {!hideSymptomsCauses && (
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">Know the Symptoms</h2>
          <ul className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {symptomList.map((text, i) => {
              const { icon: Icon } = iconForSymptom(text, i);
              const img = clinical?.symptomImages?.[i];
              return (
                <li key={text} className="relative flex items-start gap-4 rounded-2xl border border-border bg-card p-4 shadow-soft">
                  <span className="absolute -left-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-accent text-[12px] font-bold text-accent-foreground shadow-soft">
                    {i + 1}
                  </span>
                  {img ? (
                    <span className="flex h-16 w-20 shrink-0 overflow-hidden rounded-xl bg-primary-soft">
                      <img src={img} alt={text} loading="lazy" width={160} height={128} className="h-full w-full object-cover" />
                    </span>
                  ) : (
                    <span className="flex h-16 w-20 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                      <Icon className="h-7 w-7" />
                    </span>
                  )}
                  <p className="text-sm font-semibold leading-snug text-foreground">{text}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
      )}

      {!hideSymptomsCauses && (
      <section className="bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
            {surgery.name} Causes
          </h2>
          <ul className="mx-auto mt-10 flex max-w-2xl flex-wrap justify-center gap-x-8 gap-y-8 [&>li]:w-[140px]">
            {causeList.map((text, i) => {
              const c = iconForCause(text, i);
              const Icon = c.icon;
              const img = clinical?.causeImages?.[i];
              return (
                <li key={text} className="flex flex-col items-center text-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-cause-icon ring-1 ring-border">
                    {img ? (
                      <img src={img} alt={text} loading="lazy" width={128} height={128} className="cause-icon-image h-9 w-9 object-contain" />
                    ) : (
                      <Icon className="h-7 w-7 text-cause-icon" />
                    )}
                  </span>
                  <p className="mt-3 text-[12px] font-bold leading-tight text-foreground">{text}</p>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
      )}

      {/* TYPES OF SURGERY — circular images */}
      <section className="bg-background">
        <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-14">
          <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">Types of {surgery.name}</h2>
          <ul className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-x-10 gap-y-8 [&>li]:w-[180px]">
            {typeList.map((title, i) => {
              const img = clinical?.typeImages?.[i] ?? surgeryTypes[i % surgeryTypes.length].img;
              return (
              <li key={title} className="flex flex-col items-center text-center">
                <div className="h-28 w-28 overflow-hidden rounded-full ring-4 ring-primary-soft sm:h-32 sm:w-32">
                  <img src={img} alt={title} loading="lazy" width={224} height={224} className="h-full w-full object-cover" />
                </div>
                <p className="mt-4 font-display text-sm font-bold text-foreground">{title}</p>
              </li>
              );
            })}
          </ul>
        </div>
      </section>



      {/* SURGICAL METHODS at Healic */}
      {clinical?.methods && clinical.methods.length > 0 && (
        <section className="bg-cream">
          <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-14">
            <div className="text-center">
              <h2 className="font-display text-3xl font-bold sm:text-4xl">Surgical Methods at Healic</h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
                Different patients need different approaches. Our surgeons choose the right method for your condition, age and recovery goals.
              </p>
            </div>
            <ul className="mt-10 grid gap-5 sm:grid-cols-2">
              {clinical.methods.map((m: { name: string; desc: string }) => (
                <li
                  key={m.name}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-card"
                >
                  <h3 className="font-display text-lg font-bold leading-tight text-foreground">
                    {m.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}





      {/* WHY CHOOSE HEALIC — 5-step journey */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">
              Why Choose Healic for<br />{surgery.name}
            </h2>
            {specialty && (
              <p className="mt-2 text-sm font-semibold text-muted-foreground">({specialty.name})</p>
            )}
          </div>
          <div className="mt-10 rounded-3xl bg-primary-soft/70 px-4 py-8 sm:px-8">
            <ul className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
              {journeySteps.map((step, i) => (
                <li key={step.title} className="relative flex flex-col items-center text-center">
                  <span className="absolute -top-2 left-1/2 z-10 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-card text-[12px] font-bold text-foreground shadow-soft">
                    {i + 1}
                  </span>
                  <div className="overflow-hidden rounded-2xl bg-card shadow-soft">
                    <img src={step.img} alt={step.title} loading="lazy" width={320} height={224} className="h-24 w-full object-cover sm:h-28" />
                  </div>
                  <p className="mt-3 font-display text-[13px] font-bold leading-tight text-foreground">{step.title}</p>
                  {i < journeySteps.length - 1 && (
                    <ArrowRight className="absolute right-[-18px] top-12 hidden h-5 w-5 text-accent lg:block" />
                  )}
                </li>
              ))}
            </ul>
          </div>
          {/* Stats + Key benefits unified card */}
          <div className="mt-10 overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
            <div className="grid divide-y divide-border bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <div className="px-5 py-5 text-center">
                <p className="font-display text-2xl font-bold">500+</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">Surgeries Every Month</p>
              </div>
              <div className="px-5 py-5 text-center">
                <p className="font-display text-2xl font-bold">Average 4.8</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">Google Rating</p>
              </div>
              <div className="px-5 py-5 text-center">
                <p className="font-display text-2xl font-bold">85+</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">NPS Score</p>
              </div>
            </div>
            {surgery.benefits.length > 0 && (
              <div className="px-5 py-7 sm:px-8">
                <h3 className="text-center font-display text-lg font-bold text-foreground">Key benefits at Healic</h3>
                <ul className="mx-auto mt-5 grid max-w-4xl gap-3 sm:grid-cols-2">
                  {surgery.benefits.map((b: string) => (
                    <li key={b} className="flex items-start gap-3 rounded-xl bg-primary-soft/60 px-4 py-3 transition hover:bg-primary-soft">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="text-[13px] font-medium text-foreground">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PATIENT STORIES — 4 cards */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
          <div className="text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Patient Stories</span>
            <h2 className="mt-2 font-display text-3xl font-bold sm:text-4xl">What Our Patients say</h2>
          </div>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {reviewsList.map((r) => (
              <li key={r.name} className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-soft">
                <Quote className="h-5 w-5 text-accent/40" />
                <div className="mt-2 flex items-center gap-1 text-amber-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
                <p className="mt-3 flex-1 text-[13px] leading-relaxed text-foreground/80">"{r.text}"</p>
                <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 font-display text-xs font-semibold text-primary">
                    {r.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground">Verified patient</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DOCTORS WHO PERFORM — 4 cards */}
      {doctors.length > 0 && (
        <section className="bg-background">
          <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8 lg:py-14">
            <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">
              Doctors who perform {surgery.name}
            </h2>
            {specialty && (
              <p className="mt-1 text-center text-sm font-semibold text-muted-foreground">({specialty.name})</p>
            )}
            <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {doctors.map((d) => {
                const loc = locations.find((l) => l.slug === d.locationSlug);
                const profileSlug = doctorProfileSlug(d.slug, d.name);
                return (
                  <li key={d.slug} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
                    <DoctorPortrait doctor={d} className="aspect-[4/3] overflow-hidden" imgClassName="object-top" />
                    <div className="flex flex-1 flex-col p-4">
                      <p className="font-display text-base font-bold leading-tight text-foreground">{d.name}</p>
                      <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">{d.specialty}</p>
                      {d.qualifications && (
                        <p className="mt-1 line-clamp-1 text-[12px] font-semibold text-foreground/80">{d.qualifications}</p>
                      )}
                      {d.experience && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground">{d.experience} experience</p>
                      )}
                      {loc && (
                        <p className="mt-0.5 text-[12px] text-muted-foreground">Healic {loc.city}</p>
                      )}
                      <Link
                        to="/doctors/$slug"
                        params={{ slug: profileSlug }}
                        className="mt-3 block rounded-pill bg-primary px-3 py-2 text-center text-[12px] font-semibold uppercase tracking-wide text-primary-foreground hover:opacity-90"
                      >
                        Consult Now
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* LOCATIONS & RELATED */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-3xl border border-border bg-card p-6">
            <h3 className="font-display text-xl font-semibold">Available at Healic centres</h3>
            <ul className="mt-4 space-y-2">
              {locations.map((l) => (
                <li key={l.slug}>
                  <Link
                    to="/surgeries/$slug/$location"
                    params={{ slug: surgery.slug, location: l.slug }}
                    className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-medium hover:bg-cream"
                  >
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-accent" />
                      {surgery.name} in {l.city.split(",")[0]}
                    </span>
                    <ArrowRight className="h-4 w-4 text-accent" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {related.length > 0 && (
            <div className="rounded-3xl border border-border bg-card p-6">
              <h3 className="font-display text-xl font-semibold">Related procedures</h3>
              <ul className="mt-4 space-y-2">
                {related.map((r: Surgery) => (
                  <li key={r.slug}>
                    <Link
                      to="/surgeries/$slug"
                      params={{ slug: r.slug }}
                      className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm font-medium hover:bg-cream"
                    >
                      <span>{r.name}</span>
                      <ArrowRight className="h-4 w-4 text-accent" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {extraSections && (
        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-14">
            {extraSections}
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs.length > 0 && (

        <section className="bg-gradient-to-b from-background via-primary-soft/30 to-background">
          <div className="mx-auto max-w-6xl px-4 py-12 lg:px-8 lg:py-16">
            <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
              <div className="grid lg:grid-cols-[0.9fr_1.4fr]">
                <div className="relative flex flex-col items-center justify-between gap-6 bg-primary-soft/60 p-6 text-center lg:p-8">
                  <div>
                    <span className="inline-flex items-center rounded-full bg-accent/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-accent">
                      FAQ
                    </span>
                    <h2 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl">
                      Find Answers to<br />Your Questions
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                      We are happy to answer every question you have.
                    </p>
                  </div>
                  <div className="relative flex w-full items-end justify-center">
                    <div className="absolute bottom-0 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-accent/20 blur-2xl" />
                    <HealicMascot size="lg" className="relative !h-56 !w-56 sm:!h-64 sm:!w-64 lg:!h-72 lg:!w-72" />
                  </div>
                </div>

                <div className="p-5 sm:p-7 lg:p-8">
                  <ul className="space-y-3">
                    {faqs.map((f: { q: string; a: string }, i: number) => (
                      <details
                        key={f.q}
                        open={i === 0}
                        className="group rounded-2xl border border-border bg-card p-4 open:border-accent/40 open:bg-accent/5"
                      >
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-semibold text-foreground">
                          <span className="text-sm sm:text-base">
                            <span className="mr-2 text-accent">{i + 1}.</span>
                            {f.q}
                          </span>
                          <ChevronDown className="h-4 w-4 shrink-0 text-accent transition-transform group-open:rotate-180" />
                        </summary>
                        <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                      </details>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}

